import { useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { SelectList, SelectListItem } from "@/components/ui/select-list";
import { ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { useAuth } from "@clerk/react";
import { InfoIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useCampaignStore } from "@/stores";
import { useEveActionsStore } from "@/stores/eve/eveActionsStore";
import { EvePhasePatch, EvePipelinePhase } from "@/types/eve";
import { evePhaseOrder, isCustomPhaseKey, isEvePayoutSlotKey } from "@/lib/eve";
import { phaseIcon } from "./shared";

/**
 * The pipeline "+": ONE dialog — missing built-ins as one-click adds (the
 * payout family shows a single progressive "Payment" entry, never three),
 * or a custom step (label → derived x- slug, permanent shape). New phases
 * always start DISABLED: action/wait steps can't legally enable without their
 * chat-configured tools/trigger, and review phases enable right from the
 * editor the user lands in.
 */

const BUILT_INS: { key: string; label: string }[] = [
  { key: "contract", label: "Contract" },
  { key: "script", label: "Script" },
  { key: "draft", label: "Draft" },
  { key: "posting", label: "Posting" },
];

// The payout family is ONE progressive dialog entry: the first add is the
// classic full Payment; each further add introduces the next split
// sub-payment (payment_1, then payment_2 — amy's executor refuses gaps or
// reversed order anyway), and the pipeline labels renumber themselves
// ("Payment 1" / "Payment 2" / …) once more than one payment exists.
const PAYOUT_ADD_ORDER = ["payment", "payment_1", "payment_2"] as const;

type Shape = "review" | "action" | "wait";

const SHAPES: { shape: Shape; title: string; description: string }[] = [
  { shape: "review", title: "Review", description: "Creator submits work for review." },
  { shape: "action", title: "Action", description: "Amy runs tools — no creator email." },
  { shape: "wait", title: "Wait", description: "Hold until an event or teammate releases." },
];

/** label → x-<slug>, dodging the reserved -review/-run/-wait suffixes. */
function deriveKey(label: string): string {
  let body = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 23)
    .replace(/-+$/g, "");
  if (["review", "run", "wait"].some((s) => body === s || body.endsWith(`-${s}`))) {
    body = `${body.slice(0, 18)}-step`;
  }
  return body ? `x-${body}` : "";
}

export function AddPhaseDialog({
  pipeline,
  targetTiers,
  onCreated,
  trigger,
}: {
  pipeline: EvePipelinePhase[];
  /** Tiers the new phase is added to: the selected tier in the per-tier view, null = every tier. */
  targetTiers: number[] | null;
  /** Called with the new phase key after a successful create. */
  onCreated: (key: string) => void;
  trigger: React.ReactNode;
}) {
  const { campaignId } = useCampaignStore();
  const { getToken, orgId } = useAuth();
  const { updateCampaignPhases, isSavingConfig } = useEveActionsStore();

  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [shape, setShape] = useState<Shape | null>(null);

  const has = (key: string) => pipeline.some((p) => p.key === key);
  const nextPayout = PAYOUT_ADD_ORDER.find((k) => !has(k));
  const hasAnyPayout = PAYOUT_ADD_ORDER.some(has);
  const missing: { key: string; label: string }[] = [
    ...BUILT_INS.filter((b) => !has(b.key)),
    ...(nextPayout
      ? [{ key: nextPayout, label: hasAnyPayout ? "Another payment (split)" : "Payment" }]
      : []),
  ];

  // Payout adds place by THIS pipeline's actual layout, never the canonical
  // slot numbers (a reordered pipeline makes those land somewhere random):
  // the first Payment appends at the end, and a split add slips the new
  // early sub-payment into the final Payment's current position while moving
  // the final to the end — so the payment you just added always shows up at
  // the bottom of the nav (as the last ordinal), where a new phase is
  // expected, and the early-before-final invariant holds by construction.
  const maxOrder = pipeline.reduce((m, p) => Math.max(m, evePhaseOrder(p)), 30);
  const payoutPatches = (key: string): EvePhasePatch[] => {
    const finalEntry = pipeline.find((p) => p.key === "payment");
    if (key === "payment" || !finalEntry) return [{ key, order: maxOrder + 10 }];
    return [
      { key, order: evePhaseOrder(finalEntry) },
      { key: "payment", order: maxOrder + 10 },
    ];
  };
  const key = deriveKey(label);
  const keyTaken = pipeline.some((p) => p.key === key);
  const keyValid = isCustomPhaseKey(key) && !keyTaken;

  const handleOpenChange = (value: boolean) => {
    setOpen(value);
    if (!value) {
      setLabel("");
      setShape(null);
    }
  };

  const create = async (patches: EvePhasePatch[], createdKey: string) => {
    const token = await getToken();
    if (!token || !campaignId || !orgId) return console.error("No token found");
    const ok = await updateCampaignPhases(campaignId, patches, orgId, token, { tiers: targetTiers });
    if (ok) {
      handleOpenChange(false);
      onCreated(createdKey);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent className="sm:max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>Add a phase</AlertDialogTitle>
          <AlertDialogDescription>
            Extend what happens after activation — pick a built-in phase or
            create your own step.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-6">
          {missing.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-muted-foreground text-xs">Built-in</Label>
              <div className="flex flex-wrap gap-1.5">
                {missing.map((b) => {
                  const Icon = phaseIcon({ key: b.key });
                  return (
                    <Button
                      key={b.key}
                      variant="secondary"
                      size="sm"
                      disabled={isSavingConfig}
                      onClick={() =>
                        create(isEvePayoutSlotKey(b.key) ? payoutPatches(b.key) : [{ key: b.key }], b.key)
                      }
                    >
                      <Icon className="size-3.5 opacity-60" />
                      {b.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="custom_phase_label" className="text-muted-foreground text-xs">
              {missing.length > 0 ? "Or a custom step" : "Custom Phase Name"}
            </Label>
            <Input
              id="custom_phase_label"
              placeholder="e.g. Affiliate Signup"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={60}
              autoFocus
            />
            {label.trim() && (
              <p className="text-xs font-mono text-muted-foreground ml-1 animate-in fade-in-0 slide-in-from-top-1 duration-150">
                → {key || "…"}
                {keyTaken && " (already exists)"}
              </p>
            )}
          </div>
          <SelectList value={shape ?? ""} onValueChange={(v) => setShape(v as Shape)}>
            {SHAPES.map((s) => (
              <SelectListItem key={s.shape} value={s.shape}>
                <ItemContent className="gap-0">
                  <ItemTitle>{s.title}</ItemTitle>
                  <ItemDescription className="line-clamp-none">{s.description}</ItemDescription>
                </ItemContent>
              </SelectListItem>
            ))}
          </SelectList>
          <Alert variant="info">
            <AlertTitle>
              <InfoIcon />
              New phases start disabled
            </AlertTitle>
            <AlertDescription>
              Shape is permanent. Action and wait steps need setup with Amy
              before they can turn on.
            </AlertDescription>
          </Alert>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button
            disabled={!keyValid || !shape || !label.trim() || isSavingConfig}
            onClick={() => create([{ key, label: label.trim(), shape: shape! }], key)}
          >
            {isSavingConfig && <Spinner />}
            Create Step
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
